import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check } from "lucide-react";
import axios from "axios";
import { API, useRequestModal } from "../App";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const RequestModal = () => {
  const { isOpen, product, closeModal } = useRequestModal();
  const [step, setStep] = useState("form"); // form, success
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    country: "",
    other_country: "",
    message: ""
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCountryChange = (value) => {
    setFormData(prev => ({ ...prev, country: value, other_country: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!product) return;

    setLoading(true);

    try {
      await axios.post(`${API}/requests`, {
        product_id: product.id,
        product_name: product.name,
        product_sku: product.sku,
        ...formData
      });
      setStep("success");
    } catch (error) {
      console.error("Error submitting request:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep("form");
    setFormData({
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      country: "",
      other_country: "",
      message: ""
    });
    closeModal();
  };

  if (!product) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[200] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Backdrop */}
          <motion.div 
            className="absolute inset-0 bg-black/90"
            onClick={handleClose}
          />

          {/* Modal */}
          <motion.div
            className="relative bg-[#0A0A0A] border border-white/10 w-full max-w-lg max-h-[90vh] overflow-y-auto"
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.3 }}
            data-testid="request-modal"
          >
            {/* Close Button */}
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors z-10"
              data-testid="close-request-modal"
            >
              <X size={20} strokeWidth={1.5} />
            </button>

            {step === "form" ? (
              <>
                {/* Header */}
                <div className="p-6 border-b border-white/10">
                  <p className="text-xs tracking-[0.2em] uppercase text-[#C5A059] mb-2">
                    Product Inquiry
                  </p>
                  <h2 className="font-['Playfair_Display'] text-2xl text-white">
                    Submit a Request
                  </h2>
                  <p className="text-gray-500 text-sm mt-2">
                    {product.name} • {product.sku}
                  </p>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-5" data-testid="request-form">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs tracking-[0.2em] uppercase text-gray-500 mb-2">
                        First Name *
                      </label>
                      <input
                        type="text"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleChange}
                        required
                        className="w-full bg-transparent border-b border-white/20 py-3 text-white text-sm focus:outline-none focus:border-white transition-colors"
                        data-testid="request-first-name"
                      />
                    </div>
                    <div>
                      <label className="block text-xs tracking-[0.2em] uppercase text-gray-500 mb-2">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleChange}
                        required
                        className="w-full bg-transparent border-b border-white/20 py-3 text-white text-sm focus:outline-none focus:border-white transition-colors"
                        data-testid="request-last-name"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs tracking-[0.2em] uppercase text-gray-500 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full bg-transparent border-b border-white/20 py-3 text-white text-sm focus:outline-none focus:border-white transition-colors"
                      data-testid="request-email"
                    />
                  </div>

                  <div>
                    <label className="block text-xs tracking-[0.2em] uppercase text-gray-500 mb-2">
                      Phone *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full bg-transparent border-b border-white/20 py-3 text-white text-sm focus:outline-none focus:border-white transition-colors"
                      data-testid="request-phone"
                    />
                  </div>

                  <div>
                    <label className="block text-xs tracking-[0.2em] uppercase text-gray-500 mb-2">
                      Country *
                    </label>
                    <Select
                      value={formData.country}
                      onValueChange={handleCountryChange}
                      required
                    >
                      <SelectTrigger 
                        className="w-full bg-transparent border-0 border-b border-white/20 rounded-none py-3 text-white text-sm focus:ring-0 focus:border-white h-auto"
                        data-testid="request-country-trigger"
                      >
                        <SelectValue placeholder="Select your country" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0A0A0A] border-white/10" style={{ backgroundColor: '#0A0A0A' }}>
                        <SelectItem value="Italy" className="text-white hover:bg-white/10">Italy</SelectItem>
                        <SelectItem value="Switzerland" className="text-white hover:bg-white/10">Switzerland</SelectItem>
                        <SelectItem value="Germany" className="text-white hover:bg-white/10">Germany</SelectItem>
                        <SelectItem value="Russia" className="text-white hover:bg-white/10">Russia</SelectItem>
                        <SelectItem value="Other" className="text-white hover:bg-white/10">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {formData.country === "Other" && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                    >
                      <label className="block text-xs tracking-[0.2em] uppercase text-gray-500 mb-2">
                        Please specify your country *
                      </label>
                      <input
                        type="text"
                        name="other_country"
                        value={formData.other_country}
                        onChange={handleChange}
                        required
                        className="w-full bg-transparent border-b border-white/20 py-3 text-white text-sm focus:outline-none focus:border-white transition-colors"
                        data-testid="request-other-country"
                      />
                    </motion.div>
                  )}

                  <div>
                    <label className="block text-xs tracking-[0.2em] uppercase text-gray-500 mb-2">
                      Message *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={4}
                      placeholder="Tell us about your project and requirements..."
                      className="w-full bg-transparent border-b border-white/20 py-3 text-white text-sm focus:outline-none focus:border-white transition-colors resize-none placeholder:text-gray-600"
                      data-testid="request-message"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full border border-white rounded-full text-white px-8 py-4 text-sm tracking-widest uppercase font-medium hover:bg-white hover:text-black transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed mt-6"
                    data-testid="submit-request-form-btn"
                  >
                    {loading ? "Submitting..." : "Submit Request"}
                  </button>
                </form>
              </>
            ) : (
              /* Success State */
              <div className="p-12 text-center" data-testid="request-success">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", duration: 0.5 }}
                  className="w-16 h-16 mx-auto mb-6 border border-[#C5A059] rounded-full flex items-center justify-center"
                >
                  <Check size={32} className="text-[#C5A059]" strokeWidth={1.5} />
                </motion.div>
                
                <h2 className="font-['Playfair_Display'] text-2xl text-white mb-4">
                  Request Submitted
                </h2>
                <p className="text-gray-400 text-sm mb-8">
                  Thank you for your interest. We will review your request and contact you shortly.
                </p>
                
                <button
                  onClick={handleClose}
                  className="border border-white rounded-full text-white px-8 py-3 text-sm tracking-widest uppercase font-medium hover:bg-white hover:text-black transition-colors"
                  data-testid="close-success-btn"
                >
                  Close
                </button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default RequestModal;
